//React
import { Provider } from 'interfaces/provider';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useState } from 'react';

import {
  Box,
  Badge,
  Checkbox,
  Flex,
  Pill,
  PillGroup,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';

// Custom
import { DateInput } from '@mantine/dates';
import { Payor } from 'interfaces';

import {
  ProviderLicensesComponent,
  ProviderGroupComponent,
} from 'pages/providers/components';
import { getSpecialtyCodes } from 'constants/specialtyCodes';
import { Specialty } from 'interfaces/specialty';
import { useIsAdmin } from 'hooks';
import { useCurrentSiteStore } from 'store/SiteStore';
import {
  formatDateFilterToken,
  nowISODate,
  toJSDate,
  isValidDateValue,
} from 'utils/dateUtils';

export const ProviderColumns = ({ payors }: { payors: Payor[] }) => {
  const isAdmin = useIsAdmin();
  const { id: currentSiteID } = useCurrentSiteStore();

  const columns: MRT_ColumnDef<Provider>[] = [
    {
      accessorKey: 'title',
      header: 'Title',
      Edit: ({ cell }) => {
        const original = cell.row.original as Provider;
        const [title, setTitle] = useState<string>(original.title || '');
        const options: { label: string; value: string }[] = [
          { label: 'Dr.', value: 'Dr.' },
          { label: 'NP.', value: 'NP.' },
        ];
        return (
          <Select
            label="Title"
            withAsterisk
            value={title}
            onChange={(value) => {
              setTitle(value);
              cell.row._valuesCache.title = value;
            }}
            data={options}
            placeholder="Select title"
            size="sm"
          />
        );
      },
      Cell: ({ row }) => row.original.title || '',
      size: 80,
    },
    {
      accessorKey: 'firstName',
      header: 'Name',
      Edit: ({ cell }) => {
        const original = cell.row.original as Provider;
        const [firstName, setFirstName] = useState<string>(
          original.firstName || ''
        );
        return (
          <TextInput
            label="First Name"
            value={firstName}
            data-testid="firstNameTextInput"
            onChange={(e) => {
              const v = e.currentTarget.value;
              setFirstName(v);
              cell.row._valuesCache.firstName = v;
            }}
            placeholder="First Name"
            required
          />
        );
      },
      Cell: ({ row }) =>
        [row.original.firstName, row.original.lastName]
          .filter(Boolean)
          .join(' '),
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
      visibleInShowHideMenu: false,
      Edit: ({ cell }) => {
        const original = cell.row.original as Provider;
        const [lastName, setLastName] = useState<string>(
          original.lastName || ''
        );
        return (
          <TextInput
            label="Last Name"
            value={lastName}
            data-testid="lastNameTextInput"
            onChange={(e) => {
              const v = e.currentTarget.value;
              setLastName(v);
              cell.row._valuesCache.lastName = v;
            }}
            placeholder="Last Name"
            required
          />
        );
      },
      Cell: () => null,
    },
    {
      id: 'portalAccess',
      header: 'Portal Access',
      Cell: ({ row }) => {
        const hasPortalUser = !!(row.original.userID || row.original.user);
        return (
          <Badge radius="sm" color={hasPortalUser ? 'green' : 'gray'}>
            {hasPortalUser ? 'Active' : 'No user'}
          </Badge>
        );
      },
    },
    {
      visibleInShowHideMenu: false,
      id: 'createUser',
      header: 'Create User',
      Cell: () => null,
      Edit: ({ row }) => {
        return (
          <>
            {row.original.user || !isAdmin ? (
              <></>
            ) : (
              <Checkbox
                label="Create User for Provider"
                defaultChecked={false}
                onChange={(e) => {
                  row._valuesCache.createUser = e.currentTarget.checked;
                }}
                data-testid="createUserCheckbox"
              />
            )}
          </>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      visibleInShowHideMenu: false,
      Cell: () => false,
      Edit: ({ cell, row }) => {
        const [value, setValue] = useState<string>(
          cell.row.original.email || ''
        );

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          setValue(v);
          cell.row._valuesCache.email = v;
        };
        return (
          <TextInput
            label="Email"
            value={value}
            onChange={handleChange}
            disabled={row._valuesCache.createUser === false}
            placeholder="Email"
            data-testid="emailTextInput"
          />
        );
      },
    },
    {
      accessorKey: 'contactNote',
      header: 'Contact Note',
      visibleInShowHideMenu: false,
      Cell: () => null,
      Edit: ({ cell }) => {
        const original = cell.row.original as Provider;
        const [contactNote, setContactNote] = useState<string>(
          original.contactNote || ''
        );

        return (
          <Textarea
            label="Contact Note"
            description="How/when this provider prefers to be contacted (hours, method, etc.)"
            placeholder="e.g. Call between 9–11am only, prefers email for non-urgent items"
            autosize
            minRows={2}
            value={contactNote}
            data-testid="contactNoteTextInput"
            onChange={(e) => {
              const v = e.currentTarget.value;
              setContactNote(v);
              cell.row._valuesCache.contactNote = v;
            }}
          />
        );
      },
    },
    {
      accessorKey: 'specialtyStartDate',
      header: 'Specialty Start Date',
      Edit: ({ row }) => {
        const initialDateValue = toJSDate(row.original.specialtyStartDate);

        const [date, setDate] = useState<Date>(
          isValidDateValue(initialDateValue)
            ? initialDateValue
            : toJSDate(nowISODate())
        );
        const handleChange = (value: string | null) => {
          const nextDate = toJSDate(value);
          if (nextDate) setDate(nextDate);
          row._valuesCache.specialtyStartDate = formatDateFilterToken(value);
        };
        return (
          <DateInput
            value={date}
            valueFormat="YYYY-MM-DD"
            onChange={(value) => handleChange(value)}
            label="Specialty Start Date"
            data-testid="specialtyStartDateTextInput"
          />
        );
      },
      visibleInShowHideMenu: false,
    },
    {
      accessorKey: 'specialtyCode',
      header: 'Specialty',
      accessorFn: (row) => {
        return `${row.specialty?.code || ''} - ${
          row.specialty?.description || ''
        }`;
      },
      Cell: ({ row }) => {
        const code = row.original.specialty?.code || row.original.specialtyCode;
        const description = row.original.specialty?.description;

        return (
          <Stack gap={0}>
            <Text size="sm">{code || ''}</Text>
            {description && (
              <Text size="xs" c="dimmed">
                {description}
              </Text>
            )}
          </Stack>
        );
      },
      Edit: ({ row }) => {
        const availableSpecialties = getSpecialtyCodes(row.original.province);
        const [value, setValue] = useState<Specialty | null>(
          row.original.specialty || null
        );
        const handleChange = (value: string) => {
          const specialty = availableSpecialties.find(
            (candidate) => candidate.code === value
          );
          setValue(specialty);
          row._valuesCache.specialty = specialty;
        };
        return (
          <Select
            label="Specialty Code"
            data={availableSpecialties.map((specialty) => {
              return {
                label: `${specialty.description} - (${specialty.code})`,
                value: specialty.code,
              };
            })}
            data-testid="specialtyCodeSelect"
            value={value?.code}
            onChange={handleChange}
            searchable
            clearable
            onClear={() => setValue(null)}
            pb={'lg'}
            required
          />
        );
      },
    },
    {
      accessorKey: 'licenses',
      header: 'Billing Numbers',
      accessorFn: (row) => row.licenses || [],
      Cell: ({ row }) => {
        const siteLicenses =
          row.original?.licenses?.filter(
            (license) => license.siteID === currentSiteID
          ) || [];

        if (!siteLicenses.length) {
          return (
            <Text size="xs" c="dimmed">
              No billing numbers
            </Text>
          );
        }

        return (
          <Stack gap={6} miw={150}>
            {siteLicenses.map((license) => (
              <Box
                key={`${license.payorID}-${license.license}`}
                style={{
                  borderLeft: '2px solid var(--mantine-color-blue-5)',
                  paddingLeft: 8,
                }}
              >
                <Text size="sm" fw={600} lh={1.2}>
                  {license.license}
                </Text>
                <Text
                  size="xs"
                  c="dimmed"
                  lh={1.2}
                  style={{
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {license.payorName || 'Payor not set'}
                </Text>
              </Box>
            ))}
          </Stack>
        );
      },
      Edit: ({ row }) => {
        const licenses = row.original?.licenses || [];
        return (
          <ProviderLicensesComponent
            payors={payors}
            licenses={licenses}
            row={row}
            siteID={currentSiteID}
          />
        );
      },
      size: 220,
    },
    {
      accessorKey: 'groups',
      header: 'Groups',
      accessorFn: (row) =>
        (row?.groups || []).filter((g) => g.siteID === currentSiteID),
      Edit: ({ row }) => (
        <ProviderGroupComponent
          existingGroups={row.original.groups ?? []}
          providerID={row.original.id}
          sites={
            row.original.sites
              ? row.original.sites
              : [useCurrentSiteStore.getState()]
          }
          row={row}
        />
      ),
      Cell: ({ row }) => {
        const groups = row.original?.groups || [];
        return (
          <Flex gap="xs" wrap="wrap">
            {groups
              ?.filter((group) => group.siteID === currentSiteID)
              .map((group) => (
                <PillGroup key={group.id}>
                  <Pill variant="contrast">{group.name}</Pill>
                </PillGroup>
              ))}
          </Flex>
        );
      },
    },
  ];

  return columns;
};
